<script lang="ts" setup>
import type ResponseData from '@/types/ResponseData'
import CompanyDataService from '@/services/CompanyDataService'
import type Company from '@/types/Company'
import type Headquarters from '@/types/Headquarters'
import { toast } from 'vue3-toastify'
import 'vue3-toastify/dist/index.css'
import { useRouter } from "vue-router"
import { onBeforeMount, ref, computed } from 'vue'
import store from '@/store'
import AddressDataService from '@/services/AddressDataService'
import type Address from '@/types/Address'
import HeadquartersDataService from '@/services/HeadquartersDataService'

// UI primitives
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from '@/components/ui/dropdown-menu'

// Simple interface for order items
interface OrderItem {
    description: string;
    [key: string]: unknown;
}

const router = useRouter()

const currentOrder = computed(() => store.state.currentOrder)
const orderedAddress = ref({} as Address)
const currentCompany = ref({} as Company)
const items = ref([] as OrderItem[])
const vhqItem = ref()
const vhqItemPackage = ref()
const priorityItem = ref()
const vhqs = ref([] as Headquarters[])

const packages = [
    "Mini",
    "Ideal",
    "Premium",
    "Extra",
    "Individual"
]

async function getAllVhqs() {
    await HeadquartersDataService.getAllVirtualHeadquarters()
        .then((response: ResponseData) => {
            vhqs.value = response.data.data
        })
        .catch((e: Error) => {
            toast.error('Chyba: ' + e.message)
        })
}

async function getCompany(id: number) {
    await CompanyDataService.getCompanyById(id)
        .then((response: ResponseData) => {
            currentCompany.value = response.data.data
        })
        .catch((e: Error) => {
            toast.error('Chyba: ' + e.message)
        })
}

async function getCompanyAddress(id: number) {
    if (id > 0) {
        await AddressDataService.getAddressById(id)
            .then(async (response: ResponseData) => {
                orderedAddress.value = response.data.data as Address
            })
            .catch((e: Error) => {
                toast.error('Chyba: ' + e.message)
            })
    }
}

async function refreshData() {
    await getCompany(currentOrder.value.company_id)
    items.value = JSON.parse(currentOrder.value.items)
    let num = 0
    let isRenewalOrder = false
    items.value.forEach((item) => {
        // Match "Služba Virtuálne sídlo: {address_id} {name} (platnosť na 1 rok)"
        if (item.description.includes("Služba Virtuálne sídlo:")) {
            // Extract the address ID right after "Virtuálne sídlo: "
            const match = item.description.match(/Služba Virtuálne sídlo:\s*(\d+)/)
            num = match ? parseInt(match[1], 10) : 0
            // BACKWARD COMPATIBILITY: If num looks like a VHQ id instead of address id, map it
            // This handles orders created before fix (when we stored VHQ id instead of address_id)
            // Complete VHQ to address_id mapping from database
            const vhqToAddressMap: Record<number, number> = {
              1: 1,    // Ružinovská
              2: 2,    // Panorama towers
              3: 3,    // Klingerka
              4: 4,    // Miletička
              5: 5,    // SKY PARK Residence
              6: 308   // Eurovea Tower (the one that caused the bug)
            }
            if (vhqToAddressMap[num]) {
                console.log(`Backward compatibility: Converting old VHQ ID ${num} to address_id ${vhqToAddressMap[num]}`)
                num = vhqToAddressMap[num]
            }
            console.log("Address ID: " + num)
        }
        // Match "Balík služieb Virtuálneho sídla: Mini (platnosť na 1 rok)"
        if (/^Balík služieb Virtuálneho sídla[: ]/.test(item.description)) {
            vhqItem.value = item
            // Extract the package name (e.g., "Mini", "Ideal", etc.) from the description
            const match = item.description.match(/^Balík služieb Virtuálneho sídla[: ]+([A-Za-zÁÉÍÓÚÝáéíóúýčďľňôřšťžČĎĽŇÔŘŠŤŽ]+)\b/)
            console.log("Package match: " + match)
            vhqItemPackage.value = match ? match[1] : ''
        }
        // Match renewal format: "Predlženie služby Virtuálne sídlo (Mini) pre..."
        else if (item.description.includes("Predlženie služby Virtuálne sídlo")) {
            vhqItem.value = item
            isRenewalOrder = true
            // Try to extract package name from description first (backend includes it in parentheses)
            const match = item.description.match(/Virtuálne sídlo\s*\(([A-Za-zÁÉÍÓÚÝáéíóúýčďľňôřšťžČĎĽŇÔŘŠŤŽ\s]+?)\)\s+pre/)
            if (match) {
                vhqItemPackage.value = match[1].trim()
                console.log("Renewal order detected, package from description: " + vhqItemPackage.value)
            } else {
                console.log("Renewal order detected, will get package from company data")
            }
        }
        // Match priority processing upsell: "Prioritné spracovanie objednávky"
        if (item.description.includes("Prioritné spracovanie objednávky")) {
            priorityItem.value = item
        }
    })
    if (num > 0) {
        await getCompanyAddress(num)
    } else if (currentCompany.value.headquarters_id) {
        // For renewal orders, fetch address from company's headquarters
        try {
            const hqResponse: ResponseData = await HeadquartersDataService.getHeadquartersById(currentCompany.value.headquarters_id)
            const headquarters = hqResponse.data.data as Headquarters
            if (headquarters.address_id) {
                await getCompanyAddress(headquarters.address_id)
            }
        } catch (e) {
            console.error('Error fetching headquarters address:', e)
        }
    }

    // Get package from company data if not already extracted from description (covers renewal orders and edge cases where name was not stored in order items)
    if (!vhqItemPackage.value && currentCompany.value.sidlo_typ_balika) {
        // Convert database format to display format if needed
        const packageMap: Record<string, string> = {
            'MINI': 'Mini',
            'IDEAL': 'Ideal',
            'PREMIUM': 'Premium',
            'EXTRA': 'Extra',
            'INDIVIDUAL': 'Individual'
        }
        const dbPackage = currentCompany.value.sidlo_typ_balika.toUpperCase()
        vhqItemPackage.value = packageMap[dbPackage] || currentCompany.value.sidlo_typ_balika
    }
}

async function updateCompany() {
    currentCompany.value.sidlo_typ_balika = vhqItemPackage.value
    await CompanyDataService.update(currentCompany.value.id, currentCompany.value)
        .catch((e: Error) => {
            toast.error('Chyba: ' + e.message)
        })

    if (currentCompany.value.headquarters_id && vhqItem.value?.price) {
        const hqResponse: ResponseData = await HeadquartersDataService.getHeadquartersById(currentCompany.value.headquarters_id)
        const hq = hqResponse.data.data as Headquarters
        hq.price = vhqItem.value.price
        await HeadquartersDataService.update(hq.id, hq)
            .catch((e: Error) => {
                toast.error('Chyba pri ukladaní ceny sídla: ' + e.message)
            })
    }

    router.go(0)
}

onBeforeMount(async () => {
    await getAllVhqs()
    await refreshData()
})
</script>

<template>
  <Card class="max-w-2xl mx-auto shadow-xl border border-gray-100 dark:border-gray-800">
    <CardHeader class="flex flex-col gap-2">
      <div class="flex items-center gap-4">
        <Avatar class="size-12">
          <AvatarFallback class="bg-brand-50 text-brand-500">
            <svg class="size-7" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" title="Virtuálne sídlo">
              <rect x="4" y="4" width="16" height="16" rx="3" stroke="currentColor" stroke-width="2" fill="none" />
              <path d="M8 12h8" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
            </svg>
          </AvatarFallback>
        </Avatar>
        <div class="flex items-center gap-2">
          <CardTitle class="text-2xl md:text-3xl font-extrabold text-gray-900 tracking-tight dark:text-white/90">Virtuálne sídlo</CardTitle>
          <Badge v-if="vhqItem?.price" variant="solid" color="primary" class="text-xl font-semibold">
            - {{ vhqItem?.price }}€
          </Badge>
        </div>
      </div>
    </CardHeader>

    <CardContent class="flex flex-col gap-6">
      <!-- Address Section -->
      <div class="flex flex-col gap-2 bg-gray-50 rounded-lg p-4 border border-gray-100 dark:bg-white/3 dark:border-gray-800">
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Adresa sídla</label>
        <DropdownMenu>
          <DropdownMenuTrigger
            :disabled="true"
            class="bg-gray-100 border border-gray-200 rounded-md px-2 py-1 text-gray-700 cursor-not-allowed w-full flex items-center justify-between dark:bg-gray-800 dark:border-gray-800 dark:text-gray-300"
          >
            <span>
              <template v-if="orderedAddress.id">
                {{ vhqs.find(v => v.address_id === orderedAddress.id)?.name || 'Neznáma adresa' }}
              </template>
              <template v-else>
                Nevybrané
              </template>
            </span>
            <svg class="size-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/></svg>
          </DropdownMenuTrigger>
          <DropdownMenuContent class="min-w-[200px]">
            <DropdownMenuItem
              v-for="vhq in vhqs"
              :key="vhq.address_id"
              :disabled="true"
              class="data-disabled:opacity-100"
            >
              {{ vhq.name }}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <div class="text-sm text-gray-700 mt-1 flex items-center gap-2 dark:text-gray-300">
          <svg class="size-4 text-brand-400" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path d="M17.657 16.657L13.414 20.9a2 2 0 0 1-2.828 0l-4.243-4.243a8 8 0 1 1 11.314 0z" />
            <circle cx="12" cy="11" r="3" />
          </svg>
          {{ orderedAddress.street + ' ' + orderedAddress.street_number + (orderedAddress.street_number2 ? '/' + orderedAddress.street_number2 : '') + ', ' + orderedAddress.psc + ' ' + orderedAddress.city }}
        </div>
      </div>

      <!-- Package and Payment Section -->
      <div class="flex flex-col gap-4">
        <div class="flex flex-col md:flex-row gap-4">
          <div class="flex-1 flex flex-col gap-1">
            <label for="typ" class="block text-sm font-medium text-gray-700 dark:text-gray-300">Balík</label>
            <DropdownMenu>
              <DropdownMenuTrigger
                class="mt-1 block w-full shadow-xs sm:text-sm border-gray-300 rounded-md bg-white h-11 px-4 py-2.5 focus:border-brand-500 focus:ring-3 focus:ring-brand-500/20 focus-visible:border-brand-500 focus-visible:ring-3 focus-visible:ring-brand-500/20 flex items-center justify-between dark:border-gray-700 dark:bg-white/3"
                :class="{ 'border-red-400 ring-1 ring-red-400': !vhqItemPackage }"
              >
                <span>
                  <template v-if="vhqItemPackage">
                    {{ vhqItemPackage }}
                  </template>
                  <template v-else>
                    <span class="text-gray-400">-- Vyberte balík --</span>
                  </template>
                </span>
                <svg class="size-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/></svg>
              </DropdownMenuTrigger>
              <DropdownMenuContent class="min-w-[200px]">
                <DropdownMenuRadioGroup v-model="vhqItemPackage">
                  <DropdownMenuRadioItem v-for="pck in packages" :key="pck" :value="pck">
                    {{ pck }}
                  </DropdownMenuRadioItem>
                </DropdownMenuRadioGroup>
              </DropdownMenuContent>
            </DropdownMenu>
            <p v-if="!vhqItemPackage" class="mt-1 text-xs text-red-500">Balík nebol zaznamenaný v objednávke – vyberte manuálne a uložte.</p>
          </div>
          <div class="flex-1 flex flex-col gap-1">
            <label for="vzaplatenedo" class="block text-sm font-medium text-gray-700 dark:text-gray-300">Balík zaplatený do</label>
            <Input v-model="currentCompany.sidlo_zaplatene_do" name="vzaplatenedo" id="vzaplatenedo" class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 rounded-md dark:border-gray-700" />
          </div>
        </div>
        <div class="flex justify-end">
          <Button @click="updateCompany()" title="Uložiť zmeny">
            <svg class="size-5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
            Uložiť
          </Button>
        </div>
        <Alert variant="default" class="bg-transparent border-transparent p-0 shadow-none">
          <AlertDescription class="text-sm text-gray-700 dark:text-gray-300 p-0">
            <p><b>Prosim <u>prever</u> či sa sídlo aktualizovalo správne.</b></p>
          </AlertDescription>
        </Alert>
      </div>

      <!-- Additional Services Section -->
      <Alert v-if="priorityItem" variant="default" class="bg-gray-50 border border-gray-100 dark:bg-white/3 dark:border-gray-800">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-2">
            <svg class="size-4 text-blue-500" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            <AlertTitle class="text-sm font-medium text-gray-700 dark:text-gray-300">Doplnkové služby</AlertTitle>
          </div>
          <Badge variant="light" color="info" class="font-semibold">
            + {{ priorityItem.price }}€
          </Badge>
        </div>
        <AlertDescription class="text-sm text-gray-700 dark:text-gray-300">Prioritné spracovanie objednávky</AlertDescription>
      </Alert>
    </CardContent>
  </Card>
</template>